/**
 * Composant - TimeoutWarning
 *
 * Bannière d'avertissement de timeout de session (compacte, pas un modal plein écran).
 */

import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

interface TimeoutWarningProps {
  remainingSeconds: number;
  onContinue: () => void;
  onCancel: () => void;
  isVisible: boolean;
}

export function TimeoutWarning({
  remainingSeconds,
  onContinue,
  onCancel,
  isVisible,
}: TimeoutWarningProps) {
  const [seconds, setSeconds] = useState(remainingSeconds);

  useEffect(() => {
    setSeconds(remainingSeconds);
  }, [remainingSeconds]);

  useEffect(() => {
    if (!isVisible || seconds <= 0) return;

    const timer = setInterval(() => {
      setSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onCancel();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible, seconds, onCancel]);

  if (!isVisible) return null;

  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const isUrgent = seconds <= 30;
  const accentColor = isUrgent ? '#dc2626' : '#d97706';
  const bgColor = isUrgent ? '#fef2f2' : '#fffbeb';
  const borderColor = isUrgent ? '#fecaca' : '#fde68a';

  return (
    <div style={{
      background: bgColor,
      border: `1.5px solid ${borderColor}`,
      borderRadius: 14,
      padding: '14px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
    }}>
      <AlertTriangle style={{ width: 22, height: 22, color: accentColor, flexShrink: 0 }} />

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
          Session expire dans
        </span>
        <span style={{
          fontSize: '1.1rem', fontWeight: 800, fontFamily: 'monospace',
          color: accentColor,
        }}>
          {minutes}:{secs.toString().padStart(2, '0')}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onCancel}
          style={{
            padding: '6px 14px', borderRadius: 8,
            border: '1px solid #d1d5db', background: 'white',
            color: '#374151', fontSize: '0.8rem', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
        <button
          onClick={onContinue}
          style={{
            padding: '6px 14px', borderRadius: 8,
            border: 'none', background: accentColor,
            color: 'white', fontSize: '0.8rem', fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Continuer
        </button>
      </div>
    </div>
  );
}
